import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { useFeatureFlags } from "@/lib/feature-flags-context";
import { TabbedPage, TabbedPageHeader, TabConfig } from "@/components/tabbed-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvatarPicker } from "@/components/avatar-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  UserCircle,
  UsersRound,
  Plug,
  Plus,
  Loader2,
  Trash2,
  TestTube,
  Check,
  X,
  IndianRupee,
  CreditCard,
  Shield,
  MessageSquare,
  QrCode,
  Building2,
  Palette,
  Wifi,
  WifiOff,
  Play,
  AlertCircle,
  CheckCircle2,
  Copy,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Wallet,
  TrendingDown,
} from "lucide-react";
import { ThemeCard } from "@/components/theme-toggle";
import { useTheme } from "@/lib/theme-provider";

// ==================== PROFILE TAB ====================
function ProfileTab() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatarUrl || null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const getInitials = (name?: string | null) => {
    if (!name) return "??";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handleAvatarChange = async (newAvatarUrl: string, isUpload: boolean) => {
    setIsUpdating(true);
    try {
      const response = await fetch('/api/users/me/avatar', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatarUrl: newAvatarUrl }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to update avatar');
      }

      setAvatarUrl(newAvatarUrl);

      // Refresh session to update avatar everywhere
      await refreshUser();

      toast({
        title: "Avatar updated!",
        description: isUpload ? "Your custom image has been saved." : "Your avatar has been updated.",
      });
    } catch (error) {
      console.error('Avatar update error:', error);
      toast({
        title: "Update failed",
        description: "Failed to update avatar. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      await refreshUser();

      toast({
        title: "Profile updated!",
        description: "Your profile information has been saved.",
      });
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Update failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your new passwords match.",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await fetch('/api/users/me/password', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }

      toast({
        title: "Password changed!",
        description: "Your password has been updated successfully.",
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error('Password change error:', error);
      toast({
        title: "Password change failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Avatar & Basic Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Picture</CardTitle>
          <CardDescription>Update your avatar and basic information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-8">
            {/* Left Column: Avatar with Change Button */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <Avatar className="h-24 w-24 ring-2 ring-border">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt={user?.name || "User"} />}
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary font-semibold">
                    {user ? getInitials(user.name) : "??"}
                  </AvatarFallback>
                </Avatar>
                {isUpdating && (
                  <div className="absolute inset-0 bg-background/50 rounded-full flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                )}
              </div>
              <AvatarPicker
                currentAvatarUrl={avatarUrl}
                onAvatarChange={handleAvatarChange}
                userName={user?.name}
              />
            </div>

            {/* Right Column: Quick Info */}
            <div className="flex-1 space-y-3 pt-1">
              <div>
                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Full Name</Label>
                <p className="text-lg font-semibold mt-1">{user?.name || "—"}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Email Address</Label>
                <p className="text-base mt-1">{user?.email || "—"}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Account Role</Label>
                <div className="mt-1.5">
                  <Badge className="font-semibold" variant={user?.role === "SUPER" ? "default" : "secondary"}>
                    {user?.role?.replace("_", " ")}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Details Form */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Details</CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="City, State, Country"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password *</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password *</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength={6}
                />
                <p className="text-xs text-muted-foreground">At least 6 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={isChangingPassword} variant="default">
                {isChangingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Change Password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== TEAM TAB ====================
interface Admin {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

function TeamTab() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "BUSINESS_UNIT",
  });

  const { data: team, isLoading } = useQuery<Admin[]>({
    queryKey: ["team"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/settings/team");
      if (!response.ok) throw new Error("Failed to fetch team");
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/settings/team", data);
      if (!response.ok) throw new Error("Failed to create team member");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team"] });
      toast({ title: "Success", description: "Team member added" });
      setIsOpen(false);
      setFormData({ name: "", email: "", password: "", role: "BUSINESS_UNIT" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/settings/team/${id}`);
      if (!response.ok) throw new Error("Failed to delete team member");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team"] });
      toast({ title: "Success", description: "Team member removed" });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Team Members</h2>
          <p className="text-sm text-muted-foreground">{team?.length ?? 0} member(s)</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" />Add Member</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6} />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUSINESS_UNIT">Business Unit</SelectItem>
                    <SelectItem value="ORGANIZATION">Organization</SelectItem>
                    <SelectItem value="FINANCE">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : team?.length === 0 ? (
            <div className="text-center py-12">
              <UsersRound className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No team members</h3>
              <p className="text-muted-foreground">Add your first team member.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team?.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell><Badge variant="outline">{member.role.replace("_", " ")}</Badge></TableCell>
                    <TableCell><Badge variant={member.status === "ACTIVE" ? "success" : "secondary"}>{member.status}</Badge></TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(member.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== INTEGRATIONS TAB ====================
interface Integration {
  id: string;
  name: string;
  provider: string;
  type: string;
  environment: string;
  isActive: boolean;
  lastTestedAt: string | null;
  lastTestResult: string | null;
  createdAt: string;
}

const PROVIDERS: Record<string, string[]> = {
  CARD_ISSUER: ["AIRWALLEX", "NIUM", "M2P", "YAP", "ZETA", "MARQETA", "ISERVEU"],
  BANKING_PARTNER: ["ISERVEU"],
  PAYMENT_GATEWAY: ["PAYHUB"],
  KYC_PROVIDER: ["SIGNZY", "FRANKIE", "GREENID", "ONFIDO", "JUMIO", "AADHAAR_UIDAI"],
  SMS_PROVIDER: ["TWILIO", "MSG91"],
};

function IntegrationsTab() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    provider: "",
    type: "CARD_ISSUER",
    environment: "sandbox",
    apiKeyEncrypted: "",
    apiSecretEncrypted: "",
  });

  const { data: integrations, isLoading } = useQuery<Integration[]>({
    queryKey: ["integrations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/integrations");
      if (!response.ok) throw new Error("Failed to fetch integrations");
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/integrations", data);
      if (!response.ok) throw new Error("Failed to create integration");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({ title: "Success", description: "Integration added" });
      setIsOpen(false);
      setFormData({ name: "", provider: "", type: "CARD_ISSUER", environment: "sandbox", apiKeyEncrypted: "", apiSecretEncrypted: "" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await apiRequest("PATCH", `/api/integrations/${id}`, { isActive });
      if (!response.ok) throw new Error("Failed to update integration");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/integrations/${id}/test`);
      if (!response.ok) throw new Error("Test failed");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({ title: "Success", description: "Connection test passed" });
    },
    onError: () => {
      toast({ title: "Error", description: "Connection test failed", variant: "destructive" });
    },
  });

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      CARD_ISSUER: "bg-blue-500/10 text-blue-500",
      KYC_PROVIDER: "bg-green-500/10 text-green-500",
      SMS_PROVIDER: "bg-purple-500/10 text-purple-500",
      BANKING_PARTNER: "bg-amber-500/10 text-amber-500",
      PAYMENT_GATEWAY: "bg-emerald-500/10 text-emerald-500",
    };
    const labels: Record<string, string> = {
      CARD_ISSUER: "Card Issuer",
      KYC_PROVIDER: "KYC Provider",
      SMS_PROVIDER: "SMS Provider",
      BANKING_PARTNER: "Banking Partner",
      PAYMENT_GATEWAY: "Payment Gateway",
    };
    return <Badge className={colors[type] || ""}>{labels[type] || type}</Badge>;
  };

  const getProviderIcon = (provider: string) => {
    if (["ISERVEU", "M2P", "YAP", "ZETA"].includes(provider)) return <IndianRupee className="h-4 w-4" />;
    if (["PAYHUB"].includes(provider)) return <QrCode className="h-4 w-4" />;
    if (["AADHAAR_UIDAI"].includes(provider)) return <Shield className="h-4 w-4" />;
    return <CreditCard className="h-4 w-4" />;
  };

  // PayHub Testing State
  const [expandedIntegrationId, setExpandedIntegrationId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<{
    connection?: any;
    payin?: any;
    payinStatus?: any;
    payinBalance?: any;
    payoutStatus?: any;
    payoutBalance?: any;
  }>({});

  // Signzy Testing State
  const [signzyTestResults, setSignzyTestResults] = useState<{
    connection?: any;
    createMerchant?: any;
    getMerchant?: any;
  }>({});

  // Fetch PayHub status
  const { data: payhubStatusData, refetch: refetchPayhubStatus } = useQuery({
    queryKey: ["payhub-status"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/status");
      if (!response.ok) throw new Error("Failed to fetch PayHub status");
      return response.json();
    },
  });

  // Test PayHub connection
  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/test");
      if (!response.ok) throw new Error("Connection test failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, connection: data }));
      toast({ 
        title: data.connection.success ? "Connection Successful" : "Connection Failed", 
        description: data.connection.message,
        variant: data.connection.success ? "default" : "destructive",
      });
    },
    onError: (error) => {
      toast({ title: "Test Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payin Intent (UPI deep links)
  const testPayinIntentMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/india/payments/payin-intent", {
        customer_email: "test@vedalogic.io",
        phone: "+919876543210",
        username: "Test Customer",
        amount: 100, // Rs 100.00 (minimum required by PayHub)
        customerId: "test_customer",
      });
      if (!response.ok) throw new Error("Payin intent creation failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payin: data }));
      toast({ 
        title: "Payin Intent Created", 
        description: "UPI payment links generated successfully",
      });
    },
    onError: (error) => {
      toast({ title: "Payin Intent Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payin Status
  const testPayinStatusMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/payhub/payin-status", {
        transaction_id: "test_transaction_123",
        date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
      });
      if (!response.ok) throw new Error("Payin status check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payinStatus: data }));
      toast({ 
        title: "Payin Status Retrieved", 
        description: `Status: ${data.responseData?.status || 'N/A'}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payin Status Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payin Balance
  const testPayinBalanceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/payin-balance");
      if (!response.ok) throw new Error("Payin balance check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payinBalance: data }));
      toast({ 
        title: "Payin Balance Retrieved", 
        description: `Balance: Rs ${data.responseData?.balance || 0}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payin Balance Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payout Status
  const testPayoutStatusMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/payhub/payout-status", {
        transaction_id: "test_payout_123",
      });
      if (!response.ok) throw new Error("Payout status check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payoutStatus: data }));
      toast({ 
        title: "Payout Status Retrieved", 
        description: `Status: ${data.responseData?.status || 'N/A'}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payout Status Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payout Balance
  const testPayoutBalanceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/payout-balance");
      if (!response.ok) throw new Error("Payout balance check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payoutBalance: data }));
      toast({ 
        title: "Payout Balance Retrieved", 
        description: `Balance: Rs ${data.responseData?.balance || 0}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payout Balance Failed", description: String(error), variant: "destructive" });
    },
  });

  // ==================== SIGNZY MUTATIONS ====================
  
  // Test Signzy connection
  const testSignzyConnectionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/signzy/test");
      if (!response.ok) throw new Error("Connection test failed");
      return response.json();
    },
    onSuccess: (data) => {
      setSignzyTestResults((prev) => ({ ...prev, connection: data }));
      toast({ 
        title: data.connection.success ? "Connection Successful" : "Connection Failed", 
        description: data.connection.message,
        variant: data.connection.success ? "default" : "destructive",
      });
    },
    onError: (error) => {
      toast({ title: "Test Failed", description: String(error), variant: "destructive" });
    },
  });

  // Create Signzy merchant (test KYC journey)
  const createSignzyMerchantMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/signzy/create-merchant", {
        name: "Test Customer",
        email: "test@vedalogic.io",
        mobile: "+919876543210",
      });
      if (!response.ok) throw new Error("Failed to create merchant");
      return response.json();
    },
    onSuccess: (data) => {
      setSignzyTestResults((prev) => ({ ...prev, createMerchant: data }));
      toast({ 
        title: "KYC Journey Created", 
        description: `Merchant ID: ${data.merchantId}`,
      });
    },
    onError: (error) => {
      toast({ title: "Create Failed", description: String(error), variant: "destructive" });
    },
  });

  // Get Signzy merchant status
  const getSignzyMerchantMutation = useMutation({
    mutationFn: async (merchantId: string) => {
      const response = await apiRequest("GET", `/api/signzy/merchant/${merchantId}`);
      if (!response.ok) throw new Error("Failed to fetch merchant");
      return response.json();
    },
    onSuccess: (data) => {
      setSignzyTestResults((prev) => ({ ...prev, getMerchant: data }));
      toast({ 
        title: "Merchant Data Retrieved", 
        description: `Status: ${data.status}`,
      });
    },
    onError: (error) => {
      toast({ title: "Fetch Failed", description: String(error), variant: "destructive" });
    },
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Copied to clipboard" });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Integration Partners</h2>
          <p className="text-sm text-muted-foreground">Configure regional providers for card issuance, KYC, and payments</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" />Add Integration</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Integration</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4">
              <div className="space-y-2">
                <Label>Integration Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v, provider: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BANKING_PARTNER">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-amber-500" />
                        <span>Banking Partner</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="PAYMENT_GATEWAY">
                      <div className="flex items-center gap-2">
                        <QrCode className="h-4 w-4 text-emerald-500" />
                        <span>Payment Gateway</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="CARD_ISSUER">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-blue-500" />
                        <span>Card Issuer</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="KYC_PROVIDER">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-green-500" />
                        <span>KYC Provider</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="SMS_PROVIDER">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-purple-500" />
                        <span>SMS Provider</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={formData.provider} onValueChange={(v) => setFormData({ ...formData, provider: v, name: v })}>
                  <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                  <SelectContent>
                    {PROVIDERS[formData.type]?.map((p) => (
                      <SelectItem key={p} value={p}>
                        <div className="flex items-center gap-2">
                          {getProviderIcon(p)}
                          <span>{p}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Environment</Label>
                <Select value={formData.environment} onValueChange={(v) => setFormData({ ...formData, environment: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input type="password" value={formData.apiKeyEncrypted} onChange={(e) => setFormData({ ...formData, apiKeyEncrypted: e.target.value })} placeholder="sk_test_..." />
              </div>
              <Button type="submit" disabled={createMutation.isPending || !formData.provider}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Integration
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* India Partners Card */}
      <Card className="border-l-4 border-l-amber-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <IndianRupee className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-base">India Market Partners</CardTitle>
              <CardDescription>Core integration partners for India operations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/30">
                    <Building2 className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Host Server</h4>
                    <p className="text-xs text-muted-foreground mt-1">RuPay card issuance & Aadhaar KYC</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                    <IndianRupee className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">PayHub</h4>
                    <p className="text-xs text-muted-foreground mt-1">UPI payments & payouts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                    <Shield className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Aadhaar UIDAI</h4>
                    <p className="text-xs text-muted-foreground mt-1">National ID verification</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Configured Integrations Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Plug className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">Configured Integrations</CardTitle>
              <CardDescription>{integrations?.length ?? 0} integration(s) configured</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : integrations?.length === 0 ? (
            <div className="text-center py-12 border-t">
              <Plug className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No integrations configured</h3>
              <p className="text-muted-foreground">Add your first integration above.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {integrations?.map((int) => (
                  <>
                    <TableRow key={int.id} className="group">
                      <TableCell className="font-medium">{int.name}</TableCell>
                      <TableCell>{getTypeBadge(int.type)}</TableCell>
                      <TableCell><Badge variant="outline">{int.provider}</Badge></TableCell>
                      <TableCell><Badge variant={int.environment === "production" ? "default" : "secondary"}>{int.environment}</Badge></TableCell>
                      <TableCell>
                        {int.lastTestResult === "SUCCESS" ? (
                          <Badge variant="success"><Check className="mr-1 h-3 w-3" />Passed</Badge>
                        ) : int.lastTestResult === "FAILED" ? (
                          <Badge variant="destructive"><X className="mr-1 h-3 w-3" />Failed</Badge>
                        ) : (
                          <Badge variant="secondary">Not tested</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch checked={int.isActive} onCheckedChange={(checked) => toggleMutation.mutate({ id: int.id, isActive: checked })} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => testMutation.mutate(int.id)}>
                            <TestTube className="h-4 w-4" />
                          </Button>
                          {int.provider === "PAYHUB" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedIntegrationId(expandedIntegrationId === int.id ? null : int.id)}
                            >
                              {expandedIntegrationId === int.id ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* PayHub Testing Panel */}
                    {int.provider === "PAYHUB" && expandedIntegrationId === int.id && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/30 p-0">
                          <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <IndianRupee className="h-5 w-5 text-emerald-600" />
                                <h4 className="font-semibold">PayHub Testing Panel</h4>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => refetchPayhubStatus()}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh Status
                              </Button>
                            </div>

                            {/* Connection Status */}
                            <div className="p-4 rounded-lg border bg-background">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-2">
                                  {payhubStatusData?.configured ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <AlertCircle className="h-5 w-5 text-amber-500" />
                                  )}
                                  <h5 className="font-semibold text-sm">Connection Status</h5>
                                </div>
                                <Badge variant={payhubStatusData?.configured ? "success" : "secondary"}>
                                  {payhubStatusData?.configured ? "Configured" : "Not Configured"}
                                </Badge>
                              </div>
                              
                              <div className="grid gap-3 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Environment:</span>
                                  <Badge variant="outline">{payhubStatusData?.environment || "Unknown"}</Badge>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Base URL:</span>
                                  <code className="text-xs bg-muted px-2 py-1 rounded">{payhubStatusData?.baseUrl || "Not set"}</code>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Webhook Configured:</span>
                                  <Badge variant={payhubStatusData?.webhookConfigured ? "success" : "secondary"}>
                                    {payhubStatusData?.webhookConfigured ? "Yes" : "No"}
                                  </Badge>
                                </div>
                                {testResults.connection && (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Last Test:</span>
                                      <Badge variant={testResults.connection.connection.success ? "success" : "destructive"}>
                                        {testResults.connection.connection.success ? "Passed" : "Failed"}
                                      </Badge>
                                    </div>
                                    {testResults.connection.connection.latency && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Latency:</span>
                                        <span className="font-mono text-xs">{testResults.connection.connection.latency}ms</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Test Actions */}
                            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
                              {/* Connection Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                                      <Wifi className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Connection Test</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Verify API authentication</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testConnectionMutation.mutate()}
                                    disabled={testConnectionMutation.isPending}
                                  >
                                    {testConnectionMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test Connection
                                  </Button>
                                </div>
                              </div>

                              {/* Payin Intent Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                                      <IndianRupee className="h-4 w-4 text-emerald-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payin Intent</h5>
                                      <p className="text-xs text-muted-foreground mt-1">UPI deep links (Rs 100.00)</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayinIntentMutation.mutate()}
                                    disabled={testPayinIntentMutation.isPending}
                                  >
                                    {testPayinIntentMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test Payin
                                  </Button>
                                </div>
                              </div>

                              {/* Payin Balance Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30">
                                      <Wallet className="h-4 w-4 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payin Balance</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check balance</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayinBalanceMutation.mutate()}
                                    disabled={testPayinBalanceMutation.isPending}
                                  >
                                    {testPayinBalanceMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Check
                                  </Button>
                                </div>
                              </div>

                              {/* Payout Status Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/30">
                                      <TrendingDown className="h-4 w-4 text-orange-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payout Status</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check status</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayoutStatusMutation.mutate()}
                                    disabled={testPayoutStatusMutation.isPending}
                                  >
                                    {testPayoutStatusMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test
                                  </Button>
                                </div>
                              </div>

                              {/* Payout Balance Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                                      <Wallet className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payout Balance</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check balance</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayoutBalanceMutation.mutate()}
                                    disabled={testPayoutBalanceMutation.isPending}
                                  >
                                    {testPayoutBalanceMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Check
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Test Results Display */}
                            {(testResults.payin || testResults.payinStatus || testResults.payinBalance || testResults.payoutStatus || testResults.payoutBalance) && (
                              <div className="space-y-3">
                                <h5 className="font-semibold text-sm">Test Results</h5>
                                
                                {testResults.payin && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <IndianRupee className="h-4 w-4 text-emerald-600" />
                                        <span className="font-medium text-sm">Payin Request Created</span>
                                      </div>
                                      <Badge variant="success">Success</Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Amount:</span>
                                        <span className="font-mono">Rs {testResults.payin.amount}</span>
                                      </div>
                                      <div className="pt-2 border-t space-y-1">
                                        <p className="text-xs text-muted-foreground mb-2">UPI Payment Links:</p>
                                        {testResults.payin.gpay && (
                                          <a href={testResults.payin.gpay} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            📱 Google Pay →
                                          </a>
                                        )}
                                        {testResults.payin.phonepe && (
                                          <a href={testResults.payin.phonepe} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            📱 PhonePe →
                                          </a>
                                        )}
                                        {testResults.payin.paytm && (
                                          <a href={testResults.payin.paytm} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            📱 Paytm →
                                          </a>
                                        )}
                                        {testResults.payin.url && (
                                          <a href={testResults.payin.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            🔗 Generic UPI →
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {testResults.payinStatus && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                                        <span className="font-medium text-sm">Payin Status</span>
                                      </div>
                                      <Badge variant={testResults.payinStatus.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payinStatus.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payinStatus.responseData && (
                                        <>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status:</span>
                                            <Badge variant={testResults.payinStatus.responseData.status === 'success' ? "success" : "secondary"}>
                                              {testResults.payinStatus.responseData.status}
                                            </Badge>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Amount:</span>
                                            <span className="font-mono">Rs {testResults.payinStatus.responseData.amount}</span>
                                          </div>
                                          {testResults.payinStatus.responseData.utr && (
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">UTR:</span>
                                              <code className="text-xs bg-muted px-2 py-1 rounded">{testResults.payinStatus.responseData.utr}</code>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {testResults.payinBalance && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Wallet className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-sm">Payin Balance</span>
                                      </div>
                                      <Badge variant={testResults.payinBalance.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payinBalance.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payinBalance.responseData && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-muted-foreground">Available Balance:</span>
                                          <span className="font-mono text-lg font-semibold text-green-600">Rs {testResults.payinBalance.responseData.balance}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {testResults.payoutStatus && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <TrendingDown className="h-4 w-4 text-orange-600" />
                                        <span className="font-medium text-sm">Payout Status</span>
                                      </div>
                                      <Badge variant={testResults.payoutStatus.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payoutStatus.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payoutStatus.responseData && (
                                        <>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status:</span>
                                            <Badge variant={testResults.payoutStatus.responseData.status === 'success' ? "success" : "secondary"}>
                                              {testResults.payoutStatus.responseData.status}
                                            </Badge>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Amount:</span>
                                            <span className="font-mono">Rs {testResults.payoutStatus.responseData.amount}</span>
                                          </div>
                                          {testResults.payoutStatus.responseData.utr && (
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">UTR:</span>
                                              <code className="text-xs bg-muted px-2 py-1 rounded">{testResults.payoutStatus.responseData.utr}</code>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {testResults.payoutBalance && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Wallet className="h-4 w-4 text-purple-600" />
                                        <span className="font-medium text-sm">Payout Balance</span>
                                      </div>
                                      <Badge variant={testResults.payoutBalance.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payoutBalance.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payoutBalance.responseData && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-muted-foreground">Available Balance:</span>
                                          <span className="font-mono text-lg font-semibold text-purple-600">Rs {testResults.payoutBalance.responseData.balance}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Webhook Information */}
                            <div className="p-4 rounded-lg border bg-muted/50">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-2 text-sm flex-1">
                                  <p className="font-medium">Webhook Endpoint</p>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs bg-background px-2 py-1 rounded flex-1">
                                      https://james-desktop.alpaca-lionfish.ts.net/api/psps/gsx/webhook
                                    </code>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyToClipboard("https://james-desktop.alpaca-lionfish.ts.net/api/psps/gsx/webhook")}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Configure this URL in PayHub dashboard to receive payment notifications
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Signzy KYC Testing Panel */}
                    {int.provider === "SIGNZY" && expandedIntegrationId === int.id && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/30 p-0">
                          <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <Shield className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold">Signzy Video KYC Testing Panel</h4>
                              </div>
                            </div>

                            {/* Test Actions */}
                            <div className="grid gap-3 md:grid-cols-3">
                              {/* Connection Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                                      <Wifi className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Connection Test</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Verify API authentication</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testSignzyConnectionMutation.mutate()}
                                    disabled={testSignzyConnectionMutation.isPending}
                                  >
                                    {testSignzyConnectionMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test Connection
                                  </Button>
                                </div>
                              </div>

                              {/* Create Merchant Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                                      <Users className="h-4 w-4 text-emerald-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Create Merchant</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Start KYC journey</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => createSignzyMerchantMutation.mutate()}
                                    disabled={createSignzyMerchantMutation.isPending}
                                  >
                                    {createSignzyMerchantMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Create
                                  </Button>
                                </div>
                              </div>

                              {/* Get Status Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                                      <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Get Status</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check merchant</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                      if (signzyTestResults.createMerchant?.merchantId) {
                                        getSignzyMerchantMutation.mutate(signzyTestResults.createMerchant.merchantId);
                                      } else {
                                        toast({ title: "No Merchant", description: "Create a merchant first", variant: "destructive" });
                                      }
                                    }}
                                    disabled={getSignzyMerchantMutation.isPending}
                                  >
                                    {getSignzyMerchantMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Check
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Test Results Display */}
                            {(signzyTestResults.connection || signzyTestResults.createMerchant || signzyTestResults.getMerchant) && (
                              <div className="space-y-3">
                                <h5 className="font-semibold text-sm">Test Results</h5>
                                
                                {signzyTestResults.connection && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-sm">Connection Test</span>
                                      </div>
                                      <Badge variant={signzyTestResults.connection.connection.success ? "success" : "destructive"}>
                                        {signzyTestResults.connection.connection.success ? "Connected" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Environment:</span>
                                        <Badge variant="outline">{signzyTestResults.connection.environment || "Unknown"}</Badge>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Message:</span>
                                        <span className="text-xs">{signzyTestResults.connection.connection.message}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {signzyTestResults.createMerchant && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Users className="h-4 w-4 text-emerald-600" />
                                        <span className="font-medium text-sm">Merchant Created</span>
                                      </div>
                                      <Badge variant="success">Success</Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground">Merchant ID:</span>
                                        <div className="flex items-center gap-2">
                                          <code className="text-xs bg-muted px-2 py-1 rounded">{signzyTestResults.createMerchant.merchantId}</code>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0"
                                            onClick={() => copyToClipboard(signzyTestResults.createMerchant.merchantId)}
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Signzy App ID:</span>
                                        <span className="font-mono text-xs">{signzyTestResults.createMerchant.signzyAppId}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Status:</span>
                                        <Badge variant="secondary">{signzyTestResults.createMerchant.status}</Badge>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Flow:</span>
                                        <span className="text-xs">{signzyTestResults.createMerchant.flowName}</span>
                                      </div>
                                      {signzyTestResults.createMerchant.autoLoginUrl && (
                                        <div className="pt-2 border-t space-y-1">
                                          <p className="text-xs text-muted-foreground mb-2">Customer KYC URL:</p>
                                          <div className="flex items-center gap-2">
                                            <a 
                                              href={signzyTestResults.createMerchant.autoLoginUrl} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="text-xs text-primary hover:underline flex-1 truncate"
                                            >
                                              {signzyTestResults.createMerchant.shortAutoLoginUrl}
                                            </a>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 w-6 p-0"
                                              onClick={() => copyToClipboard(signzyTestResults.createMerchant.autoLoginUrl)}
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {signzyTestResults.getMerchant && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                        <span className="font-medium text-sm">Merchant Status</span>
                                      </div>
                                      <Badge variant={signzyTestResults.getMerchant.status === 'accepted' ? "success" : "secondary"}>
                                        {signzyTestResults.getMerchant.status}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Name:</span>
                                        <span className="text-xs">{signzyTestResults.getMerchant.name}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Email:</span>
                                        <span className="text-xs">{signzyTestResults.getMerchant.email}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Flow:</span>
                                        <span className="text-xs">{signzyTestResults.getMerchant.flowName}</span>
                                      </div>
                                      {signzyTestResults.getMerchant.videoKyc && signzyTestResults.getMerchant.videoKyc.length > 0 && (
                                        <div className="pt-2 border-t space-y-1">
                                          <p className="text-xs text-muted-foreground mb-2">Video KYC Available:</p>
                                          <a 
                                            href={signzyTestResults.getMerchant.videoKyc[0].videoUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="text-xs text-primary hover:underline block"
                                          >
                                            🎥 View Video Recording →
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Webhook Information */}
                            <div className="p-4 rounded-lg border bg-muted/50">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-2 text-sm flex-1">
                                  <p className="font-medium">Webhook Endpoint</p>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs bg-background px-2 py-1 rounded flex-1">
                                      https://vl-vc-middlelayer.vercel.app/api/webhooks/signzy
                                    </code>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyToClipboard("https://vl-vc-middlelayer.vercel.app/api/webhooks/signzy")}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Configure this URL in Signzy dashboard to receive KYC status updates
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== APPEARANCE TAB ====================
function AppearanceTab() {
  const { theme, setTheme } = useTheme();
  const { flags, setFlag } = useFeatureFlags();

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>Theme</CardTitle>
          <CardDescription>
            Select your preferred color scheme for the interface
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <ThemeCard
              themeValue="light"
              currentTheme={theme}
              onSelect={setTheme}
            />
            <ThemeCard
              themeValue="dark"
              currentTheme={theme}
              onSelect={setTheme}
            />
            <ThemeCard
              themeValue="system"
              currentTheme={theme}
              onSelect={setTheme}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Display Preferences</CardTitle>
          <CardDescription>
            Additional display settings (coming soon)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Compact Mode</p>
              <p className="text-xs text-muted-foreground">Reduce spacing and padding throughout the interface</p>
            </div>
            <Switch disabled />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Reduced Motion</p>
              <p className="text-xs text-muted-foreground">Minimize animations for accessibility</p>
            </div>
            <Switch disabled />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Feature Flags</CardTitle>
          <CardDescription>
            Enable or disable features across the platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Inventory</p>
              <p className="text-xs text-muted-foreground">Show the Inventory section in the sidebar navigation</p>
            </div>
            <Switch
              checked={flags.inventory}
              onCheckedChange={(checked) => setFlag("inventory", checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN SETTINGS PAGE ====================
export default function Settings() {
  const { user } = useAuth();
  const role = user?.role;

  const tabs: TabConfig[] = [
    {
      id: "profile",
      label: "Profile",
      icon: UserCircle,
      content: <ProfileTab />,
    },
    {
      id: "appearance",
      label: "Appearance",
      icon: Palette,
      content: <AppearanceTab />,
    },
    // Team management — SUPER + ORGANIZATION only
    ...(role === "SUPER" || role === "ORGANIZATION"
      ? [
          {
            id: "team",
            label: "Team",
            icon: UsersRound,
            content: <TeamTab />,
          },
        ]
      : []),
    // Integrations — SUPER + ORGANIZATION only
    ...(role === "SUPER" || role === "ORGANIZATION"
      ? [
          {
            id: "integrations",
            label: "Integrations",
            icon: Plug,
            content: <IntegrationsTab />,
          },
        ]
      : []),
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <TabbedPageHeader
        title="Settings"
        description="Manage your account, team, and integrations"
      />
      <TabbedPage tabs={tabs} defaultTab="profile" />
    </div>
  );
}
